import { useState, useEffect } from "react";
import { axiosInstance } from "../api/api";
import { LocationsType } from "../types";
import { usePermission } from "../hooks/usePermission";

export const useGetLocations = () => {
  const [locations, setLocations] = useState<LocationsType[]>([]);
  const [locationsLoading, setLocationsLoading] = useState<boolean>(false);
  const [lastFetchDay, setLastFetchDay] = useState<string>('');
  const { canIndex: canAccessLocations } = usePermission('patrol_user_index');

  // AUTO-CLEAR
  useEffect(() => {
    const checkDayChange = () => {
      const today = new Date().toDateString();
      const lastDay = localStorage.getItem('last_fetch_day');
      
      if (!lastDay) {
        localStorage.setItem('last_fetch_day', today);
        return;
      }
      
      if (lastDay !== today) {
        localStorage.setItem('last_fetch_day', today);
        setLocations([]);
        setLastFetchDay(today);
      }
    };

    checkDayChange();
  }, []);

  const formatTime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const isToday = (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const parseTimeToMinutes = (timeString: string): number => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

const getLocations = async () => {
  if (!canAccessLocations) {
    setLocations([]);
    return;
  }
  
  const patrolGroupId = localStorage.getItem("patrol_group_id");
  if (!patrolGroupId) {
    setLocations([]);
    return;
  }

  const groupName = localStorage.getItem('patrol_group_name') || `Group ${patrolGroupId}`;
  
  const patrolIdsJson = localStorage.getItem("patrol_ids");
  const allPatrolIds = patrolIdsJson ? JSON.parse(patrolIdsJson) : [];
  const fallbackPatrolId = localStorage.getItem('patrol_id');
  if (allPatrolIds.length === 0 && fallbackPatrolId) {
    allPatrolIds.push(fallbackPatrolId);
  }

  if (allPatrolIds.length === 0) {
    setLocations([]);
    return;
  }

  const today = new Date().toDateString();
  const lastDay = localStorage.getItem('last_fetch_day');
  if (lastDay !== today) {
    setLocations([]);
    localStorage.setItem('last_fetch_day', today);
  }

  setLocationsLoading(true);

  try {
    const allSpots: LocationsType[] = [];
    const scannedDataMap = new Map();

    let patrolSchedules = new Map();
    try {
      const patrolRes = await axiosInstance.get(`/api/v1/patrol/index`, {
        params: { 
          type: 'collection'
        }
      });
      
      const allPatrols = patrolRes.data || [];
      
      allPatrols.forEach((patrol: any) => {
        if (patrol.id && patrol.in) {
          patrolSchedules.set(patrol.id, {
            in: patrol.in,
            inMinutes: parseTimeToMinutes(patrol.in),
            name: patrol.name
          });
        }
      });
    } catch (error) {
      // Silent fail
    }
    try {
      const routesRes = await axiosInstance.get(
        "/api/v1/patrol/user/route/index",
        { 
          params: { 
            patrol_group_id: patrolGroupId, 
            type: 'collection'
          } 
        }
      );

      const userRoutes = routesRes.data || [];

      for (const route of userRoutes) {
        try {
          const componentsRes = await axiosInstance.get(
            `/api/v1/patrol/user/route/component/${route.id}/index`
          );
          
          const components = componentsRes.data.data || [];
          
          components.forEach((component: any) => {
            const patrolId = component.component?.patrol_id;
            const spotId = component.component?.patrol_asset_id;
            
            if (patrolId && spotId && isToday(component.created_at)) {
              const key = `${patrolId}-${spotId}`;
              scannedDataMap.set(key, component);
            }
          });
          
        } catch (error) {
          // Silent fail
        }
      }
    } catch (error) {
      // Silent fail
    }

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    for (const patrolId of allPatrolIds) {
      try {
        const schedule = patrolSchedules.get(patrolId);
        
        if (schedule) {
          const hasStarted = currentTime >= schedule.inMinutes;
          
          if (!hasStarted) {
            continue;
          }

          const spotsRes = await axiosInstance.get(
            `/api/v1/patrol/component/${patrolId}/index`,
            { 
              params: { 
                type: 'collection'
              } 
            }
          );

          const patrolSpots = spotsRes.data || [];

          patrolSpots.forEach((spot: any) => {
            const spotData = spot.asset || {};
            const spotPatrolId = spot.patrol_id;
            const spotId = spot.patrol_asset_id;
            const key = `${spotPatrolId}-${spotId}`;
            const scannedData = scannedDataMap.get(key);

            const baseSpot: LocationsType = {
              id: `${spot.id}-${spotPatrolId}`,
              patrolId: spotPatrolId,
              spotId: spotId,
              groupName: groupName,
              patrolName: schedule.name,
              spotName: spotData.name || 'Unknown Spot',
              coordinates: {
                latitude: parseFloat(spotData.latitude) || 0,
                longitude: parseFloat(spotData.longitude) || 0,
              },
              status: scannedData ? 'scanned' : 'pending',
              isScanned: !!scannedData,
              tapTime: scannedData?.created_at || '',
              formattedTapTime: scannedData ? formatTime(scannedData.created_at) : '',
              timestamp: scannedData?.created_at ? new Date(scannedData.created_at).getTime() : 0,
              scannedBy: undefined,
              photos: scannedData?.files?.map((f: any) => f.url) || []
            };

            if (scannedData?.created_by) {
              const createdBy = scannedData.created_by;
              if (createdBy?.employee?.employee_personal?.full_name) {
                baseSpot.scannedBy = createdBy.employee.employee_personal.full_name;
              } else if (createdBy?.employee?.employee_personal?.first_name) {
                baseSpot.scannedBy = createdBy.employee.employee_personal.first_name;
              } else if (createdBy?.user?.name) {
                baseSpot.scannedBy = createdBy.user.name;
              }
            }

            allSpots.push(baseSpot);
          });

        }
      } catch (error) {
        // Silent fail
      }
    }

    const sortedLocations = allSpots.sort((a, b) => {
      if (!a.isScanned && b.isScanned) return -1;
      if (a.isScanned && !b.isScanned) return 1;
      return b.timestamp - a.timestamp;
    });
    
    setLocations(sortedLocations);
    
  } catch (error) {
    setLocations([]);
  } finally {
    setLocationsLoading(false);
  }
};
  useEffect(() => {
    if (!canAccessLocations || !localStorage.getItem("patrol_group_id")) {
      return;
    }

    const interval = setInterval(() => {
      const hasPendingSpots = locations.some(spot => !spot.isScanned);
      if (hasPendingSpots && !locationsLoading) {
        getLocations();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [canAccessLocations, locations, locationsLoading]);

  useEffect(() => {
    const handleRefresh = () => {
      getLocations();
    };

    window.addEventListener('refreshLocations', handleRefresh);
    
    return () => {
      window.removeEventListener('refreshLocations', handleRefresh);
    };
  }, []);

  useEffect(() => {
    if (canAccessLocations && localStorage.getItem("patrol_group_id")) {
      getLocations();
    } else {
      setLocations([]);
    }
  }, [canAccessLocations]);

  return { 
    locations, 
    locationsLoading, 
    refetch: getLocations,
    isEmpty: locations.length === 0,
    lastUpdated: new Date().toLocaleString('id-ID'),
    hasPermission: canAccessLocations
  };
};